import React, { useState, useEffect, useContext, useRef } from "react";
import ActionsContext from "common/ActionsContext";
import NotForOperators from "common/NotForOperators";

import styled from "styled-components";
import Request from "common/Request";
import ColumnsGrid from "common/layouts/ColumnsGrid";
import * as d3 from "d3";
import { isSyncDone } from "./api";
import { useClusterStatus } from "common/BqnContext";

const doesNothing = () => {};

const StatusResultLayout = styled.div`
  .fact {
    display: flex;
    flex-flow: row wrap;
    align-items: center;
    justify-content: space-between;
    color: #777;
    .field {
      text-align: left;
    }
    .value {
      text-align: left;
    }
    .field, .value {
      font-size: 1em;
      flex: 3ch 1 0;
    }
    &.warning {
      color: red;
    }
  }
`;
const ReconnectionLayout = styled.div`
  .inline {
    display: inline;
  }
  .error {
    color: red;
    font-size: 0.9em;
  }
`;

const higherLabelLength = (max, { label = "" }) =>
  max > label.length ? max : label.length;

const Reconnection = ({ reconnect = doesNothing, ready = true }) => {
  const [isBusy, setBusy] = useState(ready === false);

  useEffect(() => {
    setBusy(ready === false);
  }, [ready]);

  const doReconnect = () => {
    if (isBusy) {
      console.warn("already reconnecting");
      return;
    }
    setBusy(true);
    Promise.resolve(reconnect())
      .then(() => {
        setBusy(false);
      })
      .catch(() => {
        setBusy(false);
      });
  };
  return (
    <ReconnectionLayout>
      <ReconnectButton
        disabled={isBusy}
        label={isBusy ? "Syncing..." : "Sync now"}
        onClick={doReconnect}
      />
    </ReconnectionLayout>
  );
};

const classForFact = (label, value) =>
  label === "Failures since last sync" && parseInt(value) > 0 ? "warning" : "";

const formatDate = d3.timeFormat("%Y-%m-%dT%H:%M:%S%Z");

function StatusResults({
  facts = {},
  reconnect = doesNothing,
  moment = undefined,
  bqnId
}) {
  const bqnIds = Object.keys(facts).filter(id => bqnId === 'all' || id === bqnId.toString());
  const clusterStatus = useClusterStatus();
  const mode = clusterStatus?.mode;

  return (
    <>
      {bqnIds.map((bqnId) => (
        <div key={bqnId}>
          {mode === 'cluster' && <h6 className="margin-t-30"> BQN ID {bqnId}</h6>}
          <StatusResult
            reconnect={reconnect}
            facts={facts[bqnId]}
            moment={moment}
            ready={isSyncDone(facts[bqnId])}
          />
        </div>
      ))}
    </>
  );
}

const StatusResult = ({
  facts = [],
  className = "",
  reconnect = doesNothing,
  moment = undefined,
  ready = true,
}) => {
  if(!facts){
    return null;
  }
  return (
    <>
      <NotForOperators>
        <Reconnection reconnect={reconnect} ready={ready} />
      </NotForOperators>
      <StatusResultLayout
        maxLabelChars={1 + facts.reduce(higherLabelLength, 0)}
      >
        <ColumnsGrid columns={2} className={className} maxWidth="15rem" gap="2.5px" minWidth="6cm" rowGap="0">
          {facts.map(({ label, value }) => (
            <div className={`fact ${classForFact(label, value)}`} key={label}>
              <span className="field">{label}:</span>
              <span className="value">{value}</span>
            </div>
          ))}
          <div className={`fact`}>
            <span className="field">Date and time of status:</span>
            <span className="value">{formatDate(moment)}</span>
          </div>
        </ColumnsGrid>
      </StatusResultLayout>
    </>
  );
};
const Disabled = styled.span`
  color: gray;
  margin-top: 25px;
`;

const LabelButton = styled.span`
  color: var(--Text-Primary);
`;

const ReconnectButtonStyled = styled.button`
  i {
    color: var(--title-color); 
  }
  span {
    font-family: AllianceNo2-SemiBold !important;
    font-size: 14px;
    color: var(--title-color);
  }
`;

const ReportAPIDisabled = () => (
  <ColumnsGrid columns={1} maxWidth="15rem">
    <Disabled className="font-bold">Disabled</Disabled>
  </ColumnsGrid>
);

const ReconnectButton = ({ onClick, disabled = false, label = "Sync now" }) => (
  <ReconnectButtonStyled
    type="button"
    onClick={disabled === true ? null : onClick}
    disabled={disabled === true}
    className="btn btn-default waves-effect shadow-none hidden-to-operators"
  >
    <i className="material-icons">swap_vert</i>
    <span>{label}</span>
  </ReconnectButtonStyled>
);

const Status = ({ provider, reconnect, bqnId }) => {
  const actions = useContext(ActionsContext);
  const [request, setRequest] = useState(null);
  const intervalRef = useRef(null);
  // The provider carries the mode of the render that gave it, which the
  // effects do not depend on: the interval and the signal take the last
  // provider through the reference, instead of the one they were armed with.
  const providerRef = useRef(provider);

  providerRef.current = provider;

  const doLoad = () => {
    const doingRetrieval = providerRef.current().catch((error) => error);
    setRequest(doingRetrieval);
  };
  const isBqnKnown = bqnId !== undefined;
  const doReconnect = () => {
    const doingReconnect = reconnect();
    setRequest(doingReconnect);
    return doingReconnect;
  };
  // Nothing is asked while the BQN context has not loaded the BQN selected:
  // the date would not carry its option. The effects run again when it does.
  useEffect(() => {
    if (!isBqnKnown) {
      return;
    }

    doLoad();
    return actions.recv("do-load-billing-status", () => doLoad());
  }, [isBqnKnown, bqnId]);

  useEffect(() => {
    if (!isBqnKnown) {
      return;
    }

    intervalRef.current = setInterval(() => {
      doLoad();
    }, 1000)

    return () => {
      clearInterval(intervalRef.current);
    }
  }, [isBqnKnown, bqnId])
  return request === null ? null : (
    <Request during={request} keepContentWhileLoading={true}>
      {({ facts = null, moment }) => (
        <>
          {facts === null ? (
            <ReportAPIDisabled />
          ) : (
            <StatusResults
              reconnect={doReconnect}
              facts={facts}
              moment={moment}
              bqnId={bqnId}
            />
          )}
        </>
      )}
    </Request>
  );
};

export default Status;
